import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** shadcn/ui canonical `cn` helper: clsx + tailwind-merge (collapses duplicate utilities). */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a number as a percent; "—" when not finite. */
export function fmtPct(x: number | null | undefined, decimals = 2): string {
  if (x == null || !Number.isFinite(x)) return '—';
  return `${(x * 100).toFixed(decimals)}%`;
}

/** Signed percent (always shows +/-); fraction input (0.05 → "+5.00%"); "—" when not finite. */
export function fmtPctSigned(x: number | null | undefined, decimals = 2): string {
  if (x == null || !Number.isFinite(x)) return '—';
  return `${x >= 0 ? '+' : ''}${(x * 100).toFixed(decimals)}%`;
}

/** Whole-unit currency (no cents); "—" on null / NaN / Infinity. */
export function fmtMoney(x: number | null | undefined, currency = 'USD'): string {
  if (x == null || !Number.isFinite(x)) return '—';
  if (Math.abs(x) < 0.005) x = 0;   // avoid "-$0" from float residue
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(x);
  } catch {
    return x.toFixed(0);
  }
}

/** Currency with cents (2 fraction digits), for prices/per-share amounts; "—" on non-finite. */
export function fmtMoneyCents(x: number | null | undefined, currency = 'USD'): string {
  if (x == null || !Number.isFinite(x)) return '—';
  if (Math.abs(x) < 0.005) x = 0;   // float residue would render as "-£0.00"
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(x);
  } catch {
    return x.toFixed(2);
  }
}

/** Compact money for dense labels: £12.3K, $1.2M. */
export function fmtMoneyCompact(x: number | null | undefined, currency = 'USD'): string {
  if (x == null || !Number.isFinite(x)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1,
  }).format(x);
}

export function fmtNum(x: number | null | undefined, decimals = 2): string {
  if (x == null || !Number.isFinite(x)) return '—';
  return x.toFixed(decimals);
}

/** Parse a free-text ticker box (comma/whitespace-separated) into clean upper-cased symbols. */
export function parseTickers(input: string): string[] {
  return input.split(/[,\s]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
}

/** Tailwind text-colour for a signed value: positive → green, negative → rose, else none. */
export function toneClass(v: number | null | undefined): string {
  if (v == null) return '';
  if (v > 0) return 'text-emerald-600';
  if (v < 0) return 'text-rose-600';
  return '';
}

/** Sharpe colouring: above 1 → green, below 0 → rose, else none. */
export function sharpeToneClass(s: number | null | undefined): string {
  if (s == null) return '';
  if (s > 1) return 'text-emerald-600';
  if (s < 0) return 'text-rose-600';
  return '';
}

/** The next-longer rolling window for a label ("double the period"); falls back to `${label}×2`. */
export function doubleLabel(label: string): string {
  const m: Record<string, string> = {
    '3M': '6M',
    '6M': '1Y',
    '1Y': '2Y',
    '2Y': '4Y',
  };
  return m[label] ?? `${label}×2`;
}
